import { Link } from 'react-router-dom';
import {
  MapPin, Users, Bed, Bath, Heart, Star,
  ChevronLeft, ChevronRight, ArrowRight,
} from 'lucide-react';

const PROPERTY_IMAGES = [
  "https://images.pexels.com/photos/35264654/pexels-photo-35264654.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "https://images.unsplash.com/photo-1572177215152-32f247303126?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBob3RlbCUyMGludGVyaW9yJTIwcm9vbXxlbnwwfHx8fDE3NzU0ODA5MjR8MA&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1765775635143-6462630748ef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBob3RlbCUyMGludGVyaW9yJTIwcm9vbXxlbnwwfHx8fDE3NzU0ODA5MjR8MA&ixlib=rb-4.1.0&q=85",
  "https://images.pexels.com/photos/19737883/pexels-photo-19737883.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
];

function buildImageList(property, fallbackIndex) {
  const allImages = [];
  if (property.images?.length > 0) {
    property.images.forEach(img => {
      const url = img.storage_path ? `/api/images/${img.image_id}` : (img.url || img.image_url);
      if (url) allImages.push(url);
    });
  }
  if (property.image_url && !allImages.includes(property.image_url)) {
    allImages.unshift(property.image_url);
  }
  if (allImages.length === 0) {
    allImages.push(PROPERTY_IMAGES[fallbackIndex % PROPERTY_IMAGES.length]);
  }
  return allImages;
}

export function PropertyCard({
  property, index, currentImageIdx,
  onSlideCarousel, onToggleFavorite,
  isFavorite, rating, formatPrice, t
}) {
  const allImages = buildImageList(property, index);
  const currentIdx = currentImageIdx || 0;

  return (
    <Link to={`/properties/${property.property_id}`} className="group" data-testid={`property-card-${property.property_id}`}>
      <div className="bg-white rounded-2xl overflow-hidden card-hover h-full">
        <div className="relative h-52 overflow-hidden rounded-t-2xl">
          <img src={allImages[currentIdx]} alt={property.title}
            className="w-full h-full object-cover property-image transition-opacity duration-300"
            data-testid={`property-image-${property.property_id}`} />

          {allImages.length > 1 && (
            <>
              <button onClick={(e) => onSlideCarousel(e, property.property_id, 'prev', allImages.length)}
                className="absolute left-2 top-1/2 -translate-y-1/2 w-7 h-7 bg-white/80 backdrop-blur-sm rounded-full flex items-center justify-center shadow opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white"
                data-testid={`carousel-prev-${property.property_id}`}>
                <ChevronLeft className="w-4 h-4 text-[#1C1917]" />
              </button>
              <button onClick={(e) => onSlideCarousel(e, property.property_id, 'next', allImages.length)}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 bg-white/80 backdrop-blur-sm rounded-full flex items-center justify-center shadow opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white"
                data-testid={`carousel-next-${property.property_id}`}>
                <ChevronRight className="w-4 h-4 text-[#1C1917]" />
              </button>
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
                {allImages.slice(0, 5).map((_, dotIdx) => (
                  <span key={`dot-${dotIdx}`}
                    className={`w-1.5 h-1.5 rounded-full transition-all ${dotIdx === currentIdx % Math.min(5, allImages.length) ? 'bg-white w-3' : 'bg-white/60'}`} />
                ))}
              </div>
            </>
          )}

          <div className="absolute top-3 left-3 flex gap-1.5">
            <span className="bg-white/90 backdrop-blur-sm px-2 py-1 rounded-full text-xs font-medium text-[#264639]">
              {property.property_type || 'Hotel'}
            </span>
            {property.last_minute_offer && (
              <span className="bg-[#C36F4D] text-white px-2 py-1 rounded-full text-xs font-bold" data-testid={`offer-badge-${property.property_id}`}>
                {property.last_minute_label || 'Oferta'}{property.last_minute_discount ? ` -${property.last_minute_discount}%` : ''}
              </span>
            )}
          </div>

          <button onClick={(e) => onToggleFavorite(e, property.property_id)}
            className="absolute top-3 right-3 w-8 h-8 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-sm hover:bg-white transition-all hover:scale-110"
            data-testid={`fav-btn-${property.property_id}`}>
            <Heart className={`w-4 h-4 transition-colors ${isFavorite ? 'text-[#C36F4D] fill-[#C36F4D]' : 'text-[#57534E]'}`} />
          </button>
        </div>

        <div className="p-4">
          <div className="flex items-center text-[#78716C] text-sm mb-2">
            <MapPin className="w-4 h-4 mr-1 flex-shrink-0" />
            <span className="truncate">{property.city}{property.state ? `, ${property.state}` : ''}</span>
            <span className="ml-1 flex-shrink-0">{
              { Argentina: '\u{1F1E6}\u{1F1F7}', Brasil: '\u{1F1E7}\u{1F1F7}' }[property.country] || '\u{1F1F5}\u{1F1FE}'
            }</span>
          </div>
          <h3 className="text-base font-semibold text-[#1C1917] mb-3 line-clamp-2" style={{ fontFamily: 'Outfit' }}>
            {property.title}
          </h3>
          <div className="flex items-center gap-4 text-sm text-[#57534E] mb-3">
            <span className="flex items-center"><Users className="w-4 h-4 mr-1" /> {property.guests}</span>
            {property.bedrooms && <span className="flex items-center"><Bed className="w-4 h-4 mr-1" /> {property.bedrooms}</span>}
            {property.bathrooms && <span className="flex items-center"><Bath className="w-4 h-4 mr-1" /> {property.bathrooms}</span>}
          </div>
          {property.price_min_usd && (
            <div className="mb-1">
              {property.price_max_usd && property.price_max_usd > property.price_min_usd && (
                <span className="text-sm text-[#78716C] line-through mr-2">{formatPrice(property.price_max_usd)}</span>
              )}
              <span className="font-semibold text-brand-accent">Desde {formatPrice(property.price_min_usd)}</span>
              <span className="text-[#78716C] font-normal text-sm"> {t('common.perNight')}</span>
            </div>
          )}
          {rating && (
            <div className="flex items-center gap-1">
              <Star className="w-3.5 h-3.5 text-[#C36F4D] fill-[#C36F4D]" />
              <span className="text-sm font-medium text-[#1C1917]">{rating.avg_rating.toFixed(1)}</span>
              <span className="text-xs text-[#78716C]">({rating.count})</span>
            </div>
          )}
          <span className="inline-flex items-center text-sm font-medium mt-1 text-brand-accent">
            Ver alojamiento <ArrowRight className="w-3.5 h-3.5 ml-1" />
          </span>
        </div>
      </div>
    </Link>
  );
}
